import { Package, ExternalLink } from 'lucide-react';
import { useItemStore } from '@/store';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Props = {
  itemName: string;
  itemID: number;
};

function ItemColumn({ itemID, itemName }: Props) {
  const { openItemDialog } = useItemStore();

  const handleClick = () => {
    openItemDialog(itemID);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${itemName}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cn(
        "group flex h-16 w-full cursor-pointer items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-all duration-200",
        "hover:border-primary hover:bg-primary/5 hover:shadow-md",
        "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
        "dark:border-slate-700 dark:bg-slate-800 dark:hover:border-primary dark:hover:bg-primary/10"
      )}
    >
      {/* Item Icon */}
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/20">
        <Package className="h-5 w-5 text-primary" />
      </div>
      
      {/* Item Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="line-clamp-1 font-medium text-slate-900 dark:text-slate-100 text-sm group-hover:text-primary transition-colors">
            {itemName}
          </h3>
          <ExternalLink className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="h-5 px-2 font-mono text-xs text-slate-600 dark:text-slate-400">
            #{itemID}
          </Badge>
        </div>
      </div>
      
      {/* Hover indicator */}
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
      </div>
    </div>
  );
}

export default ItemColumn;
